import { CalendarOff, Check, Plus, Trash2, X } from 'lucide-react';
import { orgToday, orgYear } from '@/lib/today';
import * as React from 'react';

import {
  Alert, AlertDescription, Badge, Button, Card, CardContent, Combobox, Dialog,
  DialogContent, DialogFooter, DialogHeader, DialogTitle, Input, Label, Skeleton,
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Tabs, TabsContent,
  TabsList, TabsTrigger, Textarea,
} from '@/components/cargofl-ui';
import { EmptyState } from '@/components/empty-state';
import { CalendarArt } from '@/components/illustrations';
import { ConfirmButton } from '@/components/confirm-dialog';
import { PageContainer } from '@/components/shell/page-container';
import { PageHeader } from '@/components/shell/page-header';
import { ApiError, toastFailure } from '@/lib/api';
import {
  useApproveAbsence, useCancelAbsence, useLeaveBalance, useMyAbsences,
  usePendingAbsences, useRejectAbsence, useRequestAbsence,
  type Absence, type AbsenceType,
} from '@/lib/absence-api';
import { atLeast, type Role } from '@cfl/shared';

const TYPE_LABEL: Record<AbsenceType, string> = {
  VACATION: 'Annual leave',
  SICK: 'Sick leave',
  UNPAID: 'Unpaid leave',
  OTHER: 'Other',
};

const STATUS_TONE: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  APPROVED: 'default',
  REQUESTED: 'secondary',
  REJECTED: 'destructive',
};

export function AbsencePage({ role }: { role: Role }) {
  const year = orgYear();
  const [requesting, setRequesting] = React.useState(false);
  const canApprove = atLeast(role, 'SUPERVISOR');

  return (
    <PageContainer>
      <PageHeader
        title="Absence"
        description="Leave you have requested, and what it leaves you with."
        actions={
          <Button hint={"Ask for leave — your approver is notified"} onClick={() => setRequesting(true)}>
            <Plus className="size-4" />
            Request leave
          </Button>
        }
      />

      <LeaveSummary year={year} />

      {canApprove ? (
        <Tabs defaultValue="mine">
          <TabsList>
            <TabsTrigger value="mine">My absence</TabsTrigger>
            <TabsTrigger value="team">To approve</TabsTrigger>
          </TabsList>
          <TabsContent value="mine"><MyAbsences year={year} /></TabsContent>
          <TabsContent value="team"><TeamAbsences /></TabsContent>
        </Tabs>
      ) : (
        <MyAbsences year={year} />
      )}

      <RequestDialog open={requesting} onOpenChange={setRequesting} />
    </PageContainer>
  );
}

/**
 * Entitlement at a glance.
 *
 * Pending is shown next to remaining rather than folded into it: a request that has
 * not been approved is not leave yet, but somebody planning the rest of their year
 * has to see it or they will double-book themselves.
 */
function LeaveSummary({ year }: { year: number }) {
  const { data, isPending } = useLeaveBalance(year);
  if (isPending) return <Skeleton className="h-20 w-full" />;
  if (!data) return null;

  const cells: { label: string; value: string; tone?: string }[] = [
    { label: `Entitlement ${year}`, value: `${data.entitlementDays} days` },
    { label: 'Approved', value: `${data.takenDays} days` },
    { label: 'Awaiting approval', value: `${data.pendingDays} days` },
    {
      label: 'Remaining',
      value: `${data.remainingDays} days`,
      tone: data.remainingDays <= 0 ? 'text-destructive' : 'text-primary',
    },
    { label: 'Sick leave', value: `${data.sickDays} days` },
  ];

  return (
    <Card>
      <CardContent className="flex flex-wrap gap-x-10 gap-y-4 py-4">
        {cells.map((c) => (
          <div key={c.label}>
            <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {c.label}
            </div>
            <div className={`ts-num text-lg font-semibold ${c.tone ?? ''}`}>{c.value}</div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

function MyAbsences({ year }: { year: number }) {
  const { data, isPending } = useMyAbsences(year);
  const cancel = useCancelAbsence();
  const [error, setError] = React.useState<string | null>(null);

  if (isPending) return <Skeleton className="h-48 w-full" />;
  const rows = data ?? [];

  if (rows.length === 0) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <CalendarOff className="mx-auto mb-2 size-6 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">
            You have not requested any leave in {year}.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {error && (
        <Alert className="border-destructive/30">
          <AlertDescription className="text-sm">{error}</AlertDescription>
        </Alert>
      )}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="min-w-[700px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Dates</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Days</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Note</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="ts-num whitespace-nowrap font-medium">
                      {range(a)}
                    </TableCell>
                    <TableCell className="text-sm">{TYPE_LABEL[a.type]}</TableCell>
                    <TableCell className="ts-num text-right">{a.days}</TableCell>
                    <TableCell>
                      <Badge variant={STATUS_TONE[a.status] ?? 'outline'}>
                        {a.status.toLowerCase()}
                      </Badge>
                    </TableCell>
                    <TableCell className="max-w-[240px] text-sm text-muted-foreground">
                      {a.comment ?? <span className="opacity-50">—</span>}
                    </TableCell>
                    <TableCell>
                      {/* An approved absence has no cancel button: the API refuses it
                          unless you are HR, and a button that errors is worse than
                          none. The empty cell keeps the column aligned. */}
                      {a.status === 'REQUESTED' && (
                        <ConfirmButton
                          variant="ghost"
                          size="icon"
                          aria-label="Withdraw request"
                          disabled={cancel.isPending}
                          pending={cancel.isPending}
                          title="Withdraw this request?"
                          description={
                            <>
                              {a.days} {a.days === 1 ? 'day' : 'days'} of{' '}
                              {TYPE_LABEL[a.type] ?? a.type.toLowerCase()}, {a.dateFrom} to{' '}
                              {a.dateTo}. The request is removed and the days go back to your
                              balance. You can ask again afterwards.
                            </>
                          }
                          confirmLabel="Withdraw request"
                          destructive
                          onConfirm={() => {
                            setError(null);
                            cancel.mutate(a.id, {
                              onError: (e) => {
                                setError(e instanceof ApiError ? e.message : 'Could not withdraw that.');
                                toastFailure('Could not withdraw the request')(e);
                              },
                            });
                          }}
                        >
                          <Trash2 className="size-4" />
                        </ConfirmButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function TeamAbsences() {
  const { data, isPending } = usePendingAbsences();
  const approve = useApproveAbsence();
  const [rejecting, setRejecting] = React.useState<Absence | null>(null);

  if (isPending) return <Skeleton className="h-48 w-full" />;
  const rows = data ?? [];

  if (rows.length === 0) {
    return (
      <Card>
        <CardContent>
          <EmptyState art={CalendarArt} title="No leave requests are waiting on you" body="When someone in your team asks for time off, it lands here for you to approve." />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table className="min-w-[720px]">
              <TableHeader>
                <TableRow>
                  <TableHead>Person</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Days</TableHead>
                  <TableHead>Reason given</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((a) => (
                  <TableRow key={a.id}>
                    <TableCell className="font-medium">
                      {a.employee?.displayName ?? '—'}
                    </TableCell>
                    <TableCell className="ts-num whitespace-nowrap">{range(a)}</TableCell>
                    <TableCell className="text-sm">{TYPE_LABEL[a.type]}</TableCell>
                    <TableCell className="ts-num text-right">{a.days}</TableCell>
                    <TableCell className="max-w-[220px] text-sm text-muted-foreground">
                      {a.comment ?? <span className="opacity-50">—</span>}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button hint={"Approve this leave request"} variant="ghost" size="sm" disabled={approve.isPending}
                          onClick={() => approve.mutate(a.id, { onError: toastFailure(`Could not approve ${a.employee?.displayName ?? 'that'} leave`) })}>
                          <Check className="size-4" />
                          Approve
                        </Button>
                        <Button hint={"Decline this leave request, with a reason"} variant="ghost" size="sm" onClick={() => setRejecting(a)}>
                          <X className="size-4" />
                          Decline
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
      <RejectDialog absence={rejecting} onClose={() => setRejecting(null)} />
    </>
  );
}

function RequestDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const request = useRequestAbsence();
  const [type, setType] = React.useState<AbsenceType>('VACATION');
  const [from, setFrom] = React.useState('');
  const [to, setTo] = React.useState('');
  const [halfDay, setHalfDay] = React.useState(false);
  const [comment, setComment] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (!open) return;
    const today = orgToday();
    setType('VACATION');
    setFrom(today);
    setTo(today);
    setHalfDay(false);
    setComment('');
    setError(null);
  }, [open]);

  // A half day only makes sense on one date, which the API also enforces.
  const singleDay = from !== '' && from === to;
  React.useEffect(() => {
    if (!singleDay && halfDay) setHalfDay(false);
  }, [singleDay, halfDay]);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    try {
      await request.mutateAsync({ type, dateFrom: from, dateTo: to, halfDay, comment: comment.trim() || null });
      onOpenChange(false);
    } catch (err) {
      // The API's message names the actual problem - the overlap, the entitlement
      // left, the year boundary - so it is shown rather than replaced.
      setError(err instanceof ApiError ? err.message : 'Could not submit that request.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request leave</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label>Type</Label>
            <Combobox
              value={type}
              onChange={(v) => setType(v as AbsenceType)}
              options={(Object.keys(TYPE_LABEL) as AbsenceType[]).map((t) => ({
                value: t,
                label: TYPE_LABEL[t],
                hint: t === 'SICK' ? 'approved automatically' : undefined,
                description: t === 'VACATION' ? 'Counts against your annual entitlement' : undefined,
              }))}
              placeholder="Choose a type"
              searchPlaceholder="Search types"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="from">From</Label>
              <Input id="from" type="date" value={from} onChange={(e) => setFrom(e.target.value)} required />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="to">To</Label>
              <Input id="to" type="date" value={to} onChange={(e) => setTo(e.target.value)} required />
            </div>
          </div>

          {singleDay && (
            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={halfDay} onChange={(e) => setHalfDay(e.target.checked)} />
              Half day only
            </label>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="comment">Note (optional)</Label>
            <Textarea id="comment" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} />
          </div>

          <p className="text-xs leading-relaxed text-muted-foreground">
            Weekends and public holidays inside the range are not counted as leave.
            {type === 'SICK' && ' Sick leave is recorded straight away and needs no approval.'}
          </p>

          {error && (
            <Alert className="border-destructive/30">
              <AlertDescription className="text-sm leading-relaxed">{error}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button hint={"Send the request to your approver"} type="submit" disabled={request.isPending}>
              {request.isPending ? 'Submitting…' : 'Submit request'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function RejectDialog({ absence, onClose }: { absence: Absence | null; onClose: () => void }) {
  const reject = useRejectAbsence();
  const [comment, setComment] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    setComment('');
    setError(null);
  }, [absence]);

  if (!absence) return null;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (comment.trim().length < 3) {
      setError('Give a brief reason — the person only sees this.');
      return;
    }
    try {
      await reject.mutateAsync({ id: absence.id, comment: comment.trim() });
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not decline that request.');
    }
  };

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Decline {absence.employee?.displayName}&rsquo;s request</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <p className="text-sm text-muted-foreground">
            {range(absence)} · {absence.days} {absence.days === 1 ? 'day' : 'days'}
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="reject-reason">Why?</Label>
            <Textarea
              id="reject-reason"
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Two people are already off that week."
            />
          </div>
          {error && (
            <Alert className="border-destructive/30">
              <AlertDescription className="text-sm">{error}</AlertDescription>
            </Alert>
          )}
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button hint={"Decline — the person sees your reason"} type="submit" variant="destructive" disabled={reject.isPending}>
              {reject.isPending ? 'Declining…' : 'Decline request'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

const UTC = { timeZone: 'UTC' } as const;

function range(a: Absence): string {
  const f = (d: string, withYear = false) =>
    new Date(`${d}T00:00:00.000Z`).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      ...(withYear ? { year: 'numeric' } : {}),
      ...UTC,
    });
  if (a.dateFrom === a.dateTo) return `${f(a.dateFrom, true)}${a.halfDay ? ' (half)' : ''}`;
  return `${f(a.dateFrom)} – ${f(a.dateTo, true)}`;
}
